import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Divider, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { FC, useEffect } from 'react';
import CancelInsuranceStep from '../../components/steps/CancelInsuranceStep';
import LogInStep from '../../components/steps/LogInStep';
import NewInsuranceStep from '../../components/steps/NewInsuranceStep';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
  },
}));

const steps = [
  'Alte Versicherung kündigen / Neue Versicherung beantragen',
  'Auf "Mein VAV" einloggen',
];
const InsuranceStepper: FC = observer(() => {
  const [activeStep, setActiveStep] = React.useState(0);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const totalSteps = () => steps.length;

  useEffect(() => {
    const getCurrentStep = (): number => {
      const saved = localStorage.getItem('currentInsuranceStep');
      if (!saved) return 0;
      return parseInt(saved, 10);
    };

    setActiveStep(getCurrentStep());
  }, []);

  const storeCurrentStep = (step: number): void => {
    localStorage.setItem('currentInsuranceStep', JSON.stringify(step));
  };

  const isLastStep = () => activeStep === totalSteps() - 1;

  const setNewStep = (step: number) => {
    setActiveStep(step);
    storeCurrentStep(step);
  };

  const handleNext = () => {
    const newActiveStep = activeStep + 1;
    setNewStep(newActiveStep);
  };

  const handleBack = () => {
    setNewStep(activeStep - 1);
  };

  const handleStep = (step: number) => () => {
    setNewStep(step);
  };

  return (
    <Box sx={{ width: '100%', p: 6 }}>
      <Stepper nonLinear activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={activeStep > index}>
            <StepButton color="inherit" onClick={handleStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Box>
        {activeStep === 0 && (
          <StyledBox
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 6,
              mt: 4,
            }}
          >
            <NewInsuranceStep />
            <Divider
              orientation={isLargeScreen ? 'vertical' : 'horizontal'}
              flexItem
            />
            <CancelInsuranceStep />
          </StyledBox>
        )}

        {activeStep === 1 && <LogInStep />}
        <Box sx={{ display: 'flex' }}>
          {isLastStep() && (
            <Button
              color="inherit"
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{ mr: 1 }}
            >
              Zurück
            </Button>
          )}
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep === 0 && (
            <Button
              onClick={handleNext}
              sx={{ mr: 1, alignSelf: 'flex-end' }}
              variant="outlined"
              endIcon={<ArrowForward />}
            >
              Weiter
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default InsuranceStepper;
